import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import SEO from "../../components/SEO";
import PageHeader from "../../components/PageHeader";
import { Reveal } from "../../components/Reveal";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3.5 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

export default function EspaceLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user?.role === "licencie") return <Navigate to="/espace/tableau-de-bord" replace />;
  if (!loading && user?.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/espace/tableau-de-bord");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title="Connexion — Espace Licencié" description="Connectez-vous à votre espace personnel du Club Cycliste Le Boulou." />
      <PageHeader kicker="Espace Licencié" title="Connexion" />

      <section className="py-16 sm:py-24 bg-mist">
        <div className="max-w-md mx-auto px-5 sm:px-8">
          <Reveal>
            <form onSubmit={submit} data-testid="espace-login-form" className="bg-white border border-navy/10 p-8 sm:p-10">
              <div className="mb-6">
                <label className={labelCls} htmlFor="espace-email">Email</label>
                <input id="espace-email" data-testid="espace-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} autoComplete="email" />
              </div>
              <div className="mb-6">
                <label className={labelCls} htmlFor="espace-password">Mot de passe</label>
                <input id="espace-password" data-testid="espace-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoComplete="current-password" />
              </div>

              {error && (
                <p data-testid="espace-login-error" className="mb-6 bg-rouge/15 border border-rouge/40 text-rouge text-sm px-4 py-3">{error}</p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="espace-login-submit"
                className="w-full inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                Se connecter
              </button>

              <div className="mt-6 pt-6 border-t border-navy/10 flex flex-col gap-3 text-sm">
                <Link to="/espace/mot-de-passe-oublie" data-testid="espace-forgot-link" className="text-navy/70 hover:text-rouge transition-colors">
                  Mot de passe oublié ?
                </Link>
                <p className="text-steel">
                  Première connexion ?{" "}
                  <Link to="/espace/creer-acces" data-testid="espace-first-login-link" className="text-navy font-semibold hover:text-rouge transition-colors">
                    Créer mon accès
                  </Link>
                </p>
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-8 text-center text-sm text-steel">
              Pas encore membre ?{" "}
              <Link to="/rejoindre" className="text-rouge font-semibold hover:text-navy transition-colors" data-testid="espace-login-join-link">
                S'inscrire au club
              </Link>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
